import { Box, Typography, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, useMediaQuery, useTheme, ThemeProvider, InputBase, Divider } from '@mui/material'
import { ChevronLeft, MoreVert, People, Person, ExpandMore, Sync } from '@mui/icons-material'
import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { typography, spacing, layout, borderRadius, createChromeTheme, beoflowColors } from '../../theme/muiTheme'
import { useThemeMode } from '@/contexts/ThemeContext'
import { useAuth } from '@/contexts/AuthContext'
import { useNavbar } from '@/contexts/NavbarContext'
import { useAthlete } from '@/contexts/AthleteContext'
import { openIntegrationsSync } from '@/lib/integrationsSync'
import { displayName } from '@/lib/displayName'
import AgentStatusPill from '@/components/AgentStatusPill'
import { InboxIndicator } from '@/components/inbox/InboxIndicator'

interface NavbarProps {
  showBackButton?: boolean
  userName?: string
  showMenuButton?: boolean
  onMenuClick?: () => void
  sidebarOffset?: string
  /** Where the page's own content column starts, in px. The title block is
   *  centred in the space to the RIGHT of this, at the page's own measure, so
   *  the name sits exactly above the content it names. */
  contentInset?: number
  /** Width of a right-hand panel the bar should stop short of. */
  rightOffset?: string
}

export default function Navbar({
  showBackButton = false,
  userName: userNameProp,
  showMenuButton = false,
  onMenuClick,
  sidebarOffset = '0px',
  contentInset = 0,
  rightOffset = '0px',
}: NavbarProps) {
  const { user, isAuthenticated } = useAuth()
  const userName = userNameProp || displayName(user?.first_name, user?.last_name, user?.email, '')
  const { contentMaxWidth, title: navTitle, titleContent, subtitle, actions: navActions, menuItems: navMenuItems, setNavbar, modeColor, modeLabel, hideModeLabel, progress, showAthleteSelector, backButton } = useNavbar()

  // `setNavbar` merges with a spread, so a caller passing `menuItems: undefined`
  // OVERWRITES the context default of `[]` rather than leaving it alone — and every
  // read below is an unguarded `.length` / `.map`. That crashed the whole app (not
  // just the navbar) on /views/:id: revoking a share flipped ViewDetailPage's
  // menuItems to undefined and React unmounted into the ErrorBoundary.
  // Guarding here rather than at each call site: the next page to pass a bare
  // `cond ? undefined : [...]` is otherwise the next outage.
  const menuItems = navMenuItems ?? []
  const { mode: themeMode } = useThemeMode()
  const isLight = themeMode === 'light'
  // Navbar is always dark-tinted in light mode, so child components need dark-mode colors.
  // createChromeTheme, not createBeoflowTheme('dark'): dropdowns opened from the bar -- including
  // the ones pages inject through the `actions` slot -- belong to the PAGE behind them, not to
  // the 56px strip they drop from. See the note on createChromeTheme.
  const navbarTheme = useMemo(() => isLight ? createChromeTheme('light') : undefined, [isLight])

  // Athlete context
  const athleteCtx = useAthlete()

  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const hasSubtitle = !!subtitle
  // The right-hand group (athlete chip, actions, ellipsis) is flexShrink: 0 and
  // the subtitle's own controls carry fixed minWidths, so on a narrow bar neither
  // side yields and `overflow: hidden` simply cuts the subtitle off behind the
  // chip. Below sm the subtitle gets its own full-width row instead of sharing
  // one with the title: the dashboards' date adjustor alone demands ~330px, the
  // athlete chip up to 140px, and a 375px phone cannot seat both on one line.
  const stackSubtitle = useMediaQuery(theme.breakpoints.down('sm')) && hasSubtitle

  // The bar MEASURES itself rather than declaring a height for content it cannot
  // see. The subtitle is an arbitrary node supplied by the page, so any fixed
  // allowance is a guess: the old 20px was already less than the 36px-tall date
  // controls it was meant to seat, and a fixed height plus `overflow: hidden`
  // turns a few pixels of undershoot into a row that silently disappears.
  const barRef = useRef<HTMLDivElement>(null)
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null)
  useLayoutEffect(() => {
    const el = barRef.current
    if (!el) return
    // A zero height means the bar is not laid out (hidden, or jsdom), never that
    // the page wants no padding — keep the last real value rather than collapsing.
    const measure = () => setMeasuredHeight((prev) => {
      const next = el.offsetHeight
      return next > 0 && next !== prev ? next : prev
    })
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Until the first measurement lands, fall back to an estimate so the page does
  // not paint under the bar on the very first frame.
  const fallbackHeight = layout.navbarHeight + (hasSubtitle ? (stackSubtitle ? 44 : 20) : 0)
  const extraHeight = Math.max(0, (measuredHeight ?? fallbackHeight) - layout.navbarHeight)

  // Sync height offset to context so Layout can adjust padding
  useEffect(() => {
    setNavbar({ heightOffset: extraHeight })
  }, [extraHeight, setNavbar])
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [athleteAnchorEl, setAthleteAnchorEl] = useState<null | HTMLElement>(null)
  const [athleteSearch, setAthleteSearch] = useState('')
  const menuOpen = Boolean(anchorEl)
  const athleteMenuOpen = Boolean(athleteAnchorEl)
  const selectedAthlete = useMemo(
    () => athleteCtx.athletes.find((athlete) => athlete.profile_id === athleteCtx.selectedProfileId) ?? null,
    [athleteCtx.athletes, athleteCtx.selectedProfileId],
  )
  const athleteDisplay = (athlete: { name?: string | null; email?: string | null }) => athlete.name?.trim() || athlete.email?.trim() || 'Unknown athlete'

  const groupedAthletes = useMemo(() => {
    const query = athleteSearch.trim().toLowerCase()
    const groups = new Map<string, typeof athleteCtx.athletes>()
    for (const athlete of athleteCtx.athletes) {
      if (athlete.profile_id === athleteCtx.selectedProfileId) continue
      const searchable = `${athleteDisplay(athlete)} ${athlete.email ?? ''}`.toLowerCase()
      if (query && !searchable.includes(query)) continue
      const groupName = athlete.group_name || 'Ungrouped'
      groups.set(groupName, [...(groups.get(groupName) ?? []), athlete])
    }
    return [...groups.entries()]
  }, [athleteCtx.athletes, athleteCtx.selectedProfileId, athleteSearch])

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const openSyncForSelection = () => openIntegrationsSync({
    userId: selectedAthlete?.user_id,
    profileId: athleteCtx.selectedProfileId,
  })

  const content = (
    <Box
      ref={barRef}
      sx={{
        // A FLOOR, not a height: the bar must be at least as tall as it has
        // always been (56, or 76 with a subtitle sharing the title's line) and
        // then grows with whatever the page actually put in it. Layout mirrors
        // the measured height, so the two can never disagree.
        minHeight: `${layout.navbarHeight + (hasSubtitle && !stackSubtitle ? 20 : 0)}px`,
        backgroundColor: isLight ? 'rgba(10, 10, 10, 0.95)' : 'rgba(37, 37, 37, 0.6)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        color: 'text.primary',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'center',
        pl: spacing.sm,
        // 32px of right padding is dead space on a phone, and the right-hand group
        // is what overflows first.
        pr: { xs: spacing.sm, sm: spacing.xl },
        py: stackSubtitle ? '8px' : ((hasSubtitle || !!modeLabel) ? 0 : undefined),
        position: 'fixed',
        top: 0,
        left: sidebarOffset,
        zIndex: 1100,
        width: `calc(100% - ${sidebarOffset} - ${rightOffset})`,
        borderBottom: (modeColor && progress == null) ? `3px solid ${modeColor}` : 'none',
        // Horizontal only. `overflow: hidden` on both axes is what made a row
        // taller than the declared height vanish instead of pushing the bar out.
        overflowX: 'hidden',
        transition: 'left 0.3s ease, width 0.3s ease, background 0.3s ease, border-bottom 0.3s ease',
        boxShadow: isLight ? '0 2px 8px rgba(0, 0, 0, 0.15)' : '0 4px 16px -4px rgba(0, 0, 0, 0.4)',
        '@media (prefers-reduced-transparency: reduce)': {
          background: isLight ? '#121212' : '#000000',
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: 0 }}>
      {/* Left side: Menu button (mobile), Back button, avatar, name */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing.lg, flex: 1, minWidth: 0 }}>
        {showMenuButton && (
          <Box
            onClick={onMenuClick}
            sx={{
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: borderRadius.small,
              '&:hover': {
                backgroundColor: 'rgba(248, 246, 244, 0.08)',
              },
            }}
          >
            <img
              src={`${import.meta.env.BASE_URL}logomark.svg`}
              alt="Menu"
              style={{
                width: '48px',
                height: '48px',
                filter: 'brightness(0) saturate(100%) invert(56%) sepia(88%) saturate(2698%) hue-rotate(341deg) brightness(103%) contrast(105%)',
              }}
            />
          </Box>
        )}

        {/* The desktop chevron (layout prop, 'auto'), plus the page-requested
            chevron ('show') which also renders on mobile: the pages that set
            it (public-share preview) render no sidebar/drawer, so it is their
            only exit affordance (BEO-3201). 'show' hides when going back leads
            nowhere — an anonymous viewer on the tab's first page has no
            history, and the authenticated fallback (/views) sits behind
            RequireAuth: a login wall is a dead end, worse than no button.
            The click handler re-reads the index at click time. */}
        {(backButton === 'show'
          ? isAuthenticated || ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0
          : showBackButton && !isMobile && backButton !== 'hide') && (
          <IconButton
            aria-label="Go back"
            onClick={() => {
              const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
              if (idx > 0) navigate(-1)
              else navigate('/views', { replace: true })
            }}
            sx={{
              color: beoflowColors.beoWhite,
              padding: spacing.sm,
              '&:hover': {
                backgroundColor: 'rgba(248, 246, 244, 0.08)',
              },
            }}
          >
            <ChevronLeft sx={{ fontSize: '28px' }} />
          </IconButton>
        )}

        {titleContent ? (
          <Box sx={{
            display: 'flex', flexDirection: 'column', minWidth: 0, gap: 0, flex: 1, justifyContent: 'center',
            // OUT OF THE FLEX FLOW, and that is the whole point. As a flex
            // item between the back button and the actions, `mx: auto` centres
            // in the space those two leave over -- which is not the box the
            // content column centres in, so the title sat left of the workout
            // it names by half the actions' width. Absolute against the bar
            // centres against the same box the page does.
            //
            // The layer spans the bar and ignores the pointer; only the title
            // itself takes clicks, so the back button and the menu underneath
            // stay reachable.
            ...(contentMaxWidth ? {
              position: 'absolute',
              left: 0,
              right: 0,
              mx: 'auto',
              width: '100%',
              maxWidth: `calc(${contentMaxWidth}px + ${contentInset}px)`,
              pl: `${contentInset}px`,
              pointerEvents: 'none',
              '& > *': { pointerEvents: 'auto' },
            } : {}),
          }}>
            {titleContent}
            {subtitle && !stackSubtitle && (
              <Box sx={{ mt: '2px' }}>
                {subtitle}
              </Box>
            )}
            {modeLabel && modeColor && !hideModeLabel && (
              <Typography
                variant="monoSmall"
                sx={{
                  fontSize: typography.fontSizeTiny,
                  textTransform: 'uppercase',
                  letterSpacing: typography.letterLabel,
                  color: modeColor,
                  lineHeight: 1,
                  pl: spacing.sm,
                  mt: '1px',
                  mb: '-6px',
                  display: { xs: 'block', sm: 'none' },
                }}
              >
                {modeLabel}
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, gap: 0, justifyContent: 'center' }}>
            <Typography
              variant="monoBase"
              noWrap
              sx={{ fontWeight: typography.fontWeightBold, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {navTitle || userName}
            </Typography>
            {subtitle && !stackSubtitle && (
              <Box sx={{ mt: '2px' }}>
                {subtitle}
              </Box>
            )}
            {modeLabel && modeColor && !hideModeLabel && (
              <Typography
                variant="monoSmall"
                sx={{
                  fontSize: typography.fontSizeTiny,
                  textTransform: 'uppercase',
                  letterSpacing: typography.letterLabel,
                  color: modeColor,
                  lineHeight: 1,
                  mt: '1px',
                  mb: '-6px',
                  display: { xs: 'block', sm: 'none' },
                }}
              >
                {modeLabel}
              </Typography>
            )}
          </Box>
        )}

      </Box>

      {/* Right side: Agent status + Mode label (desktop) + Page actions + Menu */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing.sm, flexShrink: 0, ml: spacing.md }}>
        {/* Persistent "AI agent connected" pill — visible only while an MCP agent
            is active for this user (activity-decay). Hidden on the smallest screens. */}
        <Box sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
          <AgentStatusPill />
        </Box>
        {/* Mounted by the bar itself, not the per-page `actions` slot, so the
            count is on every account type and every page. */}
        <InboxIndicator />
        {modeLabel && modeColor && !hideModeLabel && (
          <Typography
            variant="monoSmall"
            sx={{
              fontSize: typography.fontSizeTiny,
              textTransform: 'uppercase',
              letterSpacing: typography.letterLabel,
              color: modeColor,
              lineHeight: 1,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {modeLabel}
          </Typography>
        )}
        {showAthleteSelector && athleteCtx.isCoach && (
          <Box
            onClick={(event) => setAthleteAnchorEl(event.currentTarget)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              px: spacing.md,
              height: 34, // match navbar action-button height
              borderRadius: borderRadius.round,
              backgroundColor: beoflowColors.meanBlue,
              cursor: 'pointer',
              minWidth: 0,
              maxWidth: { xs: 140, sm: 220 },
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: beoflowColors.blueDark,
              },
            }}
          >
            {athleteCtx.selectedProfileId ? (
              <People sx={{ fontSize: 14, color: beoflowColors.beoWhite }} />
            ) : (
              <Person sx={{ fontSize: 14, color: beoflowColors.beoWhite }} />
            )}
            <Typography
              noWrap
              sx={{
                fontFamily: typography.fontFamilyMonospace,
                fontSize: typography.fontSizeTiny,
                fontWeight: typography.fontWeightSemibold,
                textTransform: 'uppercase',
                letterSpacing: typography.letterButton,
                color: beoflowColors.beoWhite,
                maxWidth: { xs: 80, sm: 150 },
              }}
            >
              {selectedAthlete ? athleteDisplay(selectedAthlete) : athleteCtx.selectedProfileName ?? 'Myself'}
            </Typography>
            <ExpandMore sx={{ fontSize: 14, color: beoflowColors.beoWhite }} />
          </Box>
        )}
        {navActions}
        {menuItems.length > 0 && (
          <>
            <IconButton
              onClick={handleMenuClick}
              sx={{
                color: 'text.primary',
                padding: spacing.sm,
                borderRadius: borderRadius.round,
                backgroundColor: 'rgba(248, 246, 244, 0.06)',
                border: '1px solid rgba(248, 246, 244, 0.1)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(248, 246, 244, 0.12)',
                  border: '1px solid rgba(248, 246, 244, 0.2)',
                },
              }}
            >
              <MoreVert sx={{ fontSize: '24px' }} />
            </IconButton>
            {/* Menu rendered outside dark ThemeProvider — see below */}
          </>
        )}
      </Box>
      </Box>

      {/* Narrow bar: the subtitle gets the full width rather than competing with
          the athlete chip for what is left of the title's line. */}
      {stackSubtitle && (
        <Box sx={{ width: '100%', minWidth: 0, mt: '6px' }}>
          {subtitle}
        </Box>
      )}

      {/* Progress bar — replaces static border during workout execution */}
      {progress != null && modeColor && (
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '3px',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        }}>
          <Box sx={{
            height: '100%',
            width: `${Math.min(progress * 100, 100)}%`,
            backgroundColor: modeColor,
            transition: 'width 0.3s ease-in-out',
          }} />
        </Box>
      )}
    </Box>
  )

  // In light mode, the navbar has a dark background — wrap children in dark theme
  // so buttons, icons, and text render with light-on-dark colors.
  // The dropdown Menu is rendered OUTSIDE the dark wrapper so it uses the page theme.
  const menu = menuItems.length > 0 ? (
    <Menu
      anchorEl={anchorEl}
      open={menuOpen}
      onClose={handleMenuClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      {menuItems.map((item, i) => (
        <MenuItem
          key={i}
          disabled={item.disabled}
          onClick={() => { item.onClick(); handleMenuClose() }}
          sx={item.color ? { color: item.color } : undefined}
        >
          {item.icon && <ListItemIcon sx={item.color ? { color: item.color } : undefined}>{item.icon}</ListItemIcon>}
          <ListItemText>{item.label}</ListItemText>
        </MenuItem>
      ))}
    </Menu>
  ) : null

  const athleteMenu = showAthleteSelector && athleteCtx.isCoach ? (
    <Menu
      anchorEl={athleteAnchorEl}
      open={athleteMenuOpen}
      onClose={() => setAthleteAnchorEl(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: {
            minWidth: 220,
            maxHeight: 360,
          },
        },
      }}
    >
      <MenuItem
        selected
        onClick={() => {
          setAthleteAnchorEl(null)
        }}
      >
        <ListItemIcon>
          {selectedAthlete ? <People fontSize="small" /> : <Person fontSize="small" />}
        </ListItemIcon>
        <ListItemText>{selectedAthlete ? athleteDisplay(selectedAthlete) : 'Myself'}</ListItemText>
      </MenuItem>
      <MenuItem
        onClick={() => {
          void openSyncForSelection()
          setAthleteAnchorEl(null)
        }}
      >
        <ListItemIcon><Sync fontSize="small" /></ListItemIcon>
        <ListItemText>Sync data</ListItemText>
      </MenuItem>
      <Divider />
      <Box sx={{ px: spacing.md, py: spacing.sm }}>
        <InputBase
          autoFocus
          placeholder="Search athletes"
          value={athleteSearch}
          onChange={(event) => setAthleteSearch(event.target.value)}
          onKeyDown={(event) => event.stopPropagation()}
          sx={{
            width: '100%',
            px: spacing.sm,
            py: '4px',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: borderRadius.medium,
            fontFamily: typography.fontFamilyMonospace,
            fontSize: typography.fontSizeSm,
          }}
        />
      </Box>
      {athleteCtx.selectedProfileId !== null && (
        <MenuItem
          onClick={() => {
            athleteCtx.setSelectedProfile(null, null)
            setAthleteSearch('')
            setAthleteAnchorEl(null)
          }}
        >
          <ListItemIcon><Person fontSize="small" /></ListItemIcon>
          <ListItemText>Myself</ListItemText>
        </MenuItem>
      )}
      {groupedAthletes.map(([groupName, athletes]) => (
        <Box key={groupName}>
          <Typography variant="monoSmall" sx={{ px: spacing.md, pt: spacing.sm, pb: spacing.xs, color: 'text.secondary' }}>
            {groupName}
          </Typography>
          {athletes.map((athlete) => (
            <MenuItem
              key={athlete.profile_id}
              onClick={() => {
                athleteCtx.setSelectedProfile(athlete.profile_id, athleteDisplay(athlete))
                setAthleteSearch('')
                setAthleteAnchorEl(null)
              }}
            >
              <ListItemIcon><People fontSize="small" /></ListItemIcon>
              <ListItemText>{athleteDisplay(athlete)}</ListItemText>
            </MenuItem>
          ))}
        </Box>
      ))}
      {!athleteCtx.isLoading && athleteCtx.athletes.length === 0 && (
        <MenuItem disabled>
          <ListItemText>No athletes available</ListItemText>
        </MenuItem>
      )}
      {!athleteCtx.isLoading && athleteCtx.athletes.length > 0 && groupedAthletes.length === 0 && athleteCtx.selectedProfileId === null && (
        <MenuItem disabled>
          <ListItemText>No matching athletes</ListItemText>
        </MenuItem>
      )}
    </Menu>
  ) : null

  return (
    <>
      {navbarTheme ? <ThemeProvider theme={navbarTheme}>{content}</ThemeProvider> : content}
      {menu}
      {athleteMenu}
    </>
  )
}
